import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useNavigate } from 'react-router-dom';
import {
  CustomInput,
  CustomSelect,
  Inline,
  Modal,
  NoResult,
  Tabs,
  Text,
} from '../../..';
import { withMediaQueries } from '../../../../../app/hoc/withMediaQueries';
import { Footer, ScrollableArea } from '../../style';
import { MODAL_CLOSE } from '../../../../../app/redux/actions';
import Pagination from '../../../Pagination';
import SortBy from '../../../SortBy';
import {
  Column,
  CourseCard,
  FiltersWrapper,
  Legend,
  SortingWrapper,
  Type,
} from './style';
import {
  VISIBILITY_OPTIONS,
  getFilterLabel,
  getFilterOptions,
  getLabel,
  getSortingOptions,
} from '../../../../../utils/labels';

import { COURSE_CREATED_LIST_GET } from '../../../../../app/redux/actions/statistics';
import theme from '../../../../theme';
import routes from '../../../../../routes';
import { timestampToDate, toPercentage } from '../../../../../utils/common';

export const ModalSupervisorCoursesId = 'ModalSupervisorCourses';

const defaultTabs = [
  { value: 'courses', label: 'Corsi' },
  { value: 'events', label: 'Eventi' },
];

const ModalSupervisorCourses = ({
  mediaIsPhone,
  filters,
  courseList,
  coursePager,
  eventList,
  eventPager,
  getCourseList,
  modalClose,
}) => {
  const navigate = useNavigate();
  const scrollableAreaRef = useRef(null);
  // filters
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchValue, setSearchValue] = useState();
  // tabs
  const [tabs, setTabs] = useState(defaultTabs);
  const [activeTab, setActiveTab] = useState(defaultTabs[0]);
  // list & pager
  const [list, setList] = useState(courseList);
  const [pager, setPager] = useState({});

  useEffect(() => {
    handleGetCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetCourseList = (page) => {
    const parsedFilters = {
      ...selectedFilters,
      page,
    };
    getCourseList({ ...parsedFilters, typology: 'course' });
    getCourseList({ ...parsedFilters, typology: 'event' });
  };

  useEffect(() => {
    if (Object.keys(selectedFilters)?.length > 0) handleGetCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onClickSearch = () => {
    setSelectedFilters({ ...selectedFilters, search_text: searchValue });
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    if (key === 'categories') {
      newFilters.single_categories = value?.value;
      newFilters.parent_categories = value?.value;
    } else {
      newFilters[key] = value?.value;
    }
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => handleGetCourseList(page);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  useEffect(() => {
    if (activeTab.value === 'courses') {
      setList(courseList);
      setPager(coursePager);
    } else if (activeTab.value === 'events') {
      setList(eventList);
      setPager(eventPager);
    }
    const newTabs = [...defaultTabs];
    newTabs[0].items = coursePager?.total_items ?? 0;
    newTabs[1].items = eventPager?.total_items ?? 0;
    setTabs(newTabs);
  }, [courseList, eventList, coursePager, eventPager, activeTab]);

  return (
    <Modal
      title="Percorsi formativi"
      description="Sezione dedicata alla visualizzazione dei percorsi formativi presenti sulla piattaforma Sisfor+. Tramite il titolo del percorso formativo è possibile accedere al dettaglio del percorso formativo"
      modalId={ModalSupervisorCoursesId}
      scrollableAreaRef={scrollableAreaRef}
      mbTitle={16}
      mbDescription={32}
    >
      <FiltersWrapper>
        <CustomInput
          size={426}
          placeholder="Cosa vuoi cercare?..."
          isSearch
          value=""
          icon="search-sm"
          isClearable
          onChange={(v) => setSearchValue(v)}
          onClickIcon={onClickSearch}
          onKeyPress={(event) => {
            if (event.key === 'Enter') onClickSearch();
          }}
        />
        {(Object.keys(filters) || []).map((key, i) => (
          <CustomSelect
            size="s"
            key={`filter-${i}`}
            wrapperId={`filter-${key}`}
            placeholder={getFilterLabel(key)}
            options={getFilterOptions(filters, key)}
            onChange={(value) => handleOnChange({ key, value })}
            isClearable
          />
        ))}
        <CustomSelect
          size="s"
          wrapperId="filter-published"
          placeholder="Stato"
          options={VISIBILITY_OPTIONS}
          onChange={(v) =>
            setSelectedFilters({ ...selectedFilters, published: v?.value })
          }
          isClearable
        />
      </FiltersWrapper>
      <Tabs
        mb={0}
        tabs={tabs}
        selected={activeTab}
        onChange={(tab) => setActiveTab(tab)}
      />
      <SortingWrapper>
        <Text type="b3">{pager?.total_items || 0} percorsi formativi</Text>
        <SortBy
          options={getSortingOptions(['publication_asc', 'alphabetical'])}
          onChange={(v) => {
            setSelectedFilters({ ...selectedFilters, sort_by: v?.value });
          }}
        />
      </SortingWrapper>
      <Legend>
        <Text type="statistics-heading">Codice</Text>
        <Text type="statistics-heading">Titolo percorso formativo</Text>
        <Text type="statistics-heading">Area tematica</Text>
        {/* //? "Tutor" is ko */}
        <Text type="statistics-heading">Assegnazioni</Text>
        <Text type="statistics-heading">Fruizioni</Text>
        <Text type="statistics-heading">Completamenti</Text>
        <Text type="statistics-heading">Stato</Text>
        <Text type="statistics-heading">Data di pubblicazione</Text>
      </Legend>
      <ScrollableArea ref={scrollableAreaRef} preserveShadows>
        {list?.length > 0 ? (
          (list || []).map((course, i) => (
            <CourseCard key={`course-${i}`}>
              <Text type="b5" color={theme().colors.primaryDarkGray700}>
                {course?.code}
              </Text>
              <Column className="ellipsis" rowGap={4}>
                <Type>{getLabel(course?.typology || '-')}</Type>
                <Text
                  type="h3"
                  className="ellipsis"
                  onClick={() => {
                    navigate(
                      `${routes.courseStatistics.path}/${course?.id}/${course?.typology === 'course' ? 'attività' : 'sessioni'
                      }`
                    );
                    modalClose();
                  }}
                >
                  {course?.title}
                </Text>
              </Column>
              <Text type="b4" className="ellipsis">
                {course?.category}
              </Text>
              {/* //? "Tutor" is ko */}
              <Text type="b2">{course?.assignments ?? 0}</Text>
              <Column>
                <Inline alignItems="baseline">
                  <Text type="b2">{course?.fruitions ?? 0}</Text>
                  <Text type="b4">/{course?.assignments ?? 0}</Text>
                </Inline>
                <Text type="b5" color={theme().colors.primaryDarkGray700}>
                  {toPercentage(course?.fruitions, course?.assignments)}
                </Text>
              </Column>
              <Column>
                <Inline alignItems="baseline">
                  <Text type="b2">{course?.completions ?? 0}</Text>
                  <Text type="b4">/{course?.assignments ?? 0}</Text>
                </Inline>
                <Text type="b5" color={theme().colors.primaryDarkGray700}>
                  {toPercentage(course?.completions, course?.assignments)}
                </Text>
              </Column>
              <Text type="b4">
                {course?.published ? 'Visibile' : 'Non visibile'}
              </Text>
              <Text type="b4" color={theme().colors.primaryDarkGray700}>
                {timestampToDate(course?.publication_date)}
              </Text>
            </CourseCard>
          ))
        ) : (
          <NoResult />
        )}
      </ScrollableArea>
      <Footer pt={40} pb={48}>
        <Pagination
          pb={0}
          pager={pager}
          callBack={loadMore}
          setItemsPerPage={setItemsPerPage}
        />
      </Footer>
    </Modal>
  );
};

ModalSupervisorCourses.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  filters: PropTypes.object,
  courseList: PropTypes.array,
  coursePager: PropTypes.object,
  eventList: PropTypes.array,
  eventPager: PropTypes.object,
  // dispatch
  getCourseList: PropTypes.func.isRequired,
  modalClose: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const {
        list: courseList,
        filters,
        pager: coursePager,
      } = state.statistics.course.createdCourses.courses;
      const { list: eventList, pager: eventPager } =
        state.statistics.course.createdCourses.events;
      return { filters, courseList, coursePager, eventList, eventPager };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
      getCourseList: (filters) =>
        dispatch({ type: COURSE_CREATED_LIST_GET._REQUEST, filters }),
    })
  ),
  withMediaQueries
)(ModalSupervisorCourses);
